import React, { useState } from 'react';
import { Alert, Box, Button, Collapse, Link, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { EticketCrossCheckResult } from 'features/authorization/helpers/eticketCrossCheck';
import { formatDateTimeToShowUser } from 'features/common/helpers';
import { getCrossStateName } from 'features/authorization/helpers';
import { CrossStateEnum } from 'features/authorization/types';
import { formatTransactionAmount } from 'features/transaction-details/helpers';
import routes from 'constants/routes';

interface EticketCrossCheckPanelProps {
	result: EticketCrossCheckResult | null;
	checking: boolean;
	checkError: string | null;
	eticketValue: string;
}

export const EticketCrossCheckPanel = ({
	result,
	checking,
	checkError,
	eticketValue,
}: EticketCrossCheckPanelProps): JSX.Element | null => {
	const [historyOpen, setHistoryOpen] = useState(false);

	if (!eticketValue.trim() && !checking && !result && !checkError) {
		return null;
	}

	if (checking) {
		return (
			<Alert severity="info" sx={{ mt: 1 }}>
				Verificando cruces previos del e-ticket…
			</Alert>
		);
	}

	if (checkError) {
		return (
			<Alert severity="warning" sx={{ mt: 1 }}>
				{checkError}
			</Alert>
		);
	}

	if (!result) {
		return null;
	}

	const alertSeverity = result.severity === 'ok' ? 'success' : result.severity === 'info' ? 'info' : 'error';

	const historyGroups = result.groups.filter((g) => g.events.length > 0);

	return (
		<Box sx={{ mt: 1 }}>
			<Alert severity={alertSeverity}>
				<Typography variant="subtitle2" fontWeight="bold">
					{result.title}
				</Typography>
				<Typography variant="body2">{result.message}</Typography>
			</Alert>

			{historyGroups.length > 0 && result.severity !== 'error' && (
				<Box sx={{ mt: 0.5 }}>
					<Button size="small" onClick={() => setHistoryOpen((v) => !v)} sx={{ textTransform: 'none', px: 0 }}>
						{historyOpen ? 'Ocultar historial' : `Ver historial (${historyGroups.length})`}
					</Button>
					<Collapse in={historyOpen}>
						<Box sx={{ pl: 1, borderLeft: 2, borderColor: 'divider', mt: 0.5 }}>
							{historyGroups.map((group) => (
								<Box key={group.authorizationId} sx={{ mb: 1 }}>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
										<Typography variant="caption" fontWeight="bold">
											Tx #{group.transactionId} · Auth #{group.authorizationId} · ****
											{group.cardLastFourDigit}
										</Typography>
										<Button
											size="small"
											component={Link}
											href={routes.transactions.detailsUnified.replace(':id', String(group.transactionId))}
											target="_blank"
											rel="noopener noreferrer"
											sx={{ minWidth: 0, p: 0, fontSize: '0.7rem' }}
											endIcon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
										>
											Ver transacción
										</Button>
									</Box>
									{group.events.map((event) => {
										const isUnbind = event.state === CrossStateEnum.UNBIND || event.amount < 0;
										return (
											<Typography key={event.id} variant="caption" color="text.secondary" display="block">
												{formatDateTimeToShowUser(event.createdAt)} · {getCrossStateName(event.state as CrossStateEnum)}{' '}
												<Typography
													component="span"
													variant="caption"
													fontWeight={600}
													color={isUnbind ? 'error.main' : 'success.main'}
												>
													{event.amount > 0 ? '+' : ''}
													{formatTransactionAmount(Math.abs(event.amount), event.coinCode)}
												</Typography>
											</Typography>
										);
									})}
								</Box>
							))}
						</Box>
					</Collapse>
				</Box>
			)}

			{historyGroups.length > 0 && result.severity === 'error' && (
				<Box sx={{ mt: 0.5, pl: 1, borderLeft: 2, borderColor: 'error.light' }}>
					{historyGroups.slice(0, 3).map((group) => (
						<Box key={group.authorizationId} sx={{ mb: 0.75 }}>
							<Typography variant="caption" fontWeight="bold" display="block">
								Tx #{group.transactionId} · Auth #{group.authorizationId}
								{group.netAmount > 0
									? ` · Cruzado vigente ${formatTransactionAmount(group.netAmount, group.coinCode)}`
									: ' · Sin cruce vigente'}
							</Typography>
							<Button
								size="small"
								component={Link}
								href={routes.transactions.detailsUnified.replace(':id', String(group.transactionId))}
								target="_blank"
								rel="noopener noreferrer"
								sx={{ p: 0, minWidth: 0, fontSize: '0.7rem' }}
							>
								Abrir transacción
							</Button>
						</Box>
					))}
				</Box>
			)}
		</Box>
	);
};
